import { calcularCargaAerea, FACTOR_VOLUMETRICO_AEREO } from '@/pages/air-requests/utils/cargaAerea';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  Trash2,
  Save,
  Plane,
  User,
  Building2,
  Package,
  MapPin,
  FileText,
  AlertCircle,
  ArrowRight,
  Send,
  X as XIcon,
  Info,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Autocomplete from './Autocomplete';
import { useToast } from './ui/toaster.jsx';
import {
  getCountriesWithAirports,
  searchClients,
  getAirportsByPais,
  getAsesores,
  getActiveIncoterms,
  getActiveCommodityTypes,
} from '../pages/air-requests/Services/catalogsService.js';

/**
 * Formulario de Solicitud Aérea — versión reutilizable.
 *
 * Modos de render:
 *  - normal (default): layout con SectionCard, header propio,
 *    submit al final. Usado en /requests/new.
 *  - compact: igual que normal pero con menos padding.
 *  - modal: layout denso, sin SectionCard envolventes, con
 *    header sticky y footer sticky. Usado dentro del Dialog de
 *    /requests. En este modo el botón submit se deshabilita
 *    hasta que el form esté completo (`isFormComplete`).
 *
 * Props:
 *  - onSubmit(payload), onCancel(), submitting, submitLabel,
 *    cancelLabel, showCancelButton, hideHeader, compact, modal
 */
/** Monedas admitidas. Debe coincidir con `MONEDAS_VALOR_MERCANCIA` del DTO. */
const MONEDAS_VALOR_MERCANCIA = ['USD', 'COP', 'EUR', 'MXN'];

export default function RequestForm({
  onSubmit,
  onCancel,
  submitting = false,
  submitLabel = 'Guardar solicitud',
  cancelLabel = 'Cancelar',
  showCancelButton = true,
  hideHeader = false,
  compact = false,
  modal = false,
  modalTitle = 'Crear Solicitud Aérea',
  modalSubtitle = 'Complete todos los campos requeridos',
}) {
  const toast = useToast();

  // ============= Estado del formulario =============
  const [form, setForm] = useState({
    tipoOperacion: '',
    incotermId: '',
    valorMercancia: '',
    monedaValorMercancia: 'USD',
    descripcion: '',
    clienteId: '',
    clienteLabel: '',
    asesorId: '',
    paisOrigenId: '',
    paisOrigenLabel: '',
    aeropuertoCargaId: '',
    aeropuertoCargaLabel: '',
    paisDestinoId: '',
    paisDestinoLabel: '',
    aeropuertoDescargaId: '',
    aeropuertoDescargaLabel: '',
    numPiezas: '',
    pesoTotal: '',
    pesoVolumetrico: '',
  });

  const [incoterms, setIncoterms] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [commodityTypes, setCommodityTypes] = useState([]);
  const [paisesConAeropuertos, setPaisesConAeropuertos] = useState([]);

  const [airportsOrigen, setAirportsOrigen] = useState([]);
  const [airportsDestino, setAirportsDestino] = useState([]);
  const [loadingAirportsOrigen, setLoadingAirportsOrigen] = useState(false);
  const [loadingAirportsDestino, setLoadingAirportsDestino] = useState(false);

  const emptyDetalle = () => ({
    numPiezas: '',
    largo: '',
    ancho: '',
    alto: '',
    unidadDimension: 'cm',
    peso: '',
    unidadPeso: 'kg',
    tipoMercanciaId: '',
    apilable: 'Si',
  });
  const [detalles, setDetalles] = useState([emptyDetalle()]);

  const [errores, setErrores] = useState({});

  const totalPiezasDetalle = useMemo(
    () => detalles.reduce((s, d) => s + (Number(d.numPiezas) || 0), 0),
    [detalles],
  );
  // Mismo cálculo que el servidor (factor IATA 6000). El servidor manda:
  // acá es sólo para mostrar lo que se va a guardar.
  const carga = useMemo(() => calcularCargaAerea(detalles), [detalles]);
  const pesoTotalDetalle = carga.pesoDetalle;
  const pesoVolumetricoDetalle = carga.pesoVolumetrico;

  // ============= Carga inicial de catálogos =============
  useEffect(() => {
    (async () => {
      try {
        const [incs, ase, cmts, paises] = await Promise.all([
          getActiveIncoterms(),
          getAsesores(),
          getActiveCommodityTypes(),
          getCountriesWithAirports(),
        ]);
        setIncoterms(incs);
        setAsesores(ase.data || ase || []);
        setCommodityTypes(cmts);
        setPaisesConAeropuertos(paises?.data || []);
      } catch (e) {
        toast.error('Error al cargar catálogos', e?.message);
      }
    })();
  }, [toast]);

  // ============= Aeropuertos por país =============
  const loadAirports = useCallback(
    async (paisNombre, side) => {
      if (!paisNombre) return;
      const setter = side === 'origen' ? setAirportsOrigen : setAirportsDestino;
      const loading = side === 'origen' ? setLoadingAirportsOrigen : setLoadingAirportsDestino;
      loading(true);
      try {
        const r = await getAirportsByPais(paisNombre, 'Activo');
        setter(r?.data || []);
      } catch (e) {
        toast.error('Error al cargar aeropuertos', e?.message);
        setter([]);
      } finally {
        loading(false);
      }
    },
    [toast],
  );

  // ============= Helpers =============
  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const clearError = (key) => setErrores((e) => {
    const n = { ...e };
    delete n[key];
    return n;
  });

  useEffect(() => {
    setForm((f) => ({
      ...f,
      numPiezas: totalPiezasDetalle > 0 ? String(totalPiezasDetalle) : '',
      pesoTotal: pesoTotalDetalle > 0 ? Number(pesoTotalDetalle.toFixed(2)).toString() : '',
      pesoVolumetrico: pesoVolumetricoDetalle > 0 ? Number(pesoVolumetricoDetalle.toFixed(2)).toString() : '',
    }));
  }, [pesoTotalDetalle, pesoVolumetricoDetalle, totalPiezasDetalle]);

  // ============= Handlers de Autocomplete =============
  const handleClientChange = (id, item) => {
    setForm((f) => ({
      ...f,
      clienteId: id || '',
      clienteLabel: item ? `${item.nombre} — ${item.nit}` : '',
    }));
    clearError('clienteId');
  };
  const handlePaisOrigenChange = (id, item) => {
    const paisNombre = item?.nombre || item?._id || '';
    setForm((f) => ({
      ...f,
      paisOrigenId: paisNombre,
      paisOrigenLabel: paisNombre,
      aeropuertoCargaId: '',
      aeropuertoCargaLabel: '',
    }));
    setAirportsOrigen([]);
    if (paisNombre) loadAirports(paisNombre, 'origen');
    clearError('paisOrigenId');
  };
  const handlePaisDestinoChange = (id, item) => {
    const paisNombre = item?.nombre || item?._id || '';
    setForm((f) => ({
      ...f,
      paisDestinoId: paisNombre,
      paisDestinoLabel: paisNombre,
      aeropuertoDescargaId: '',
      aeropuertoDescargaLabel: '',
    }));
    setAirportsDestino([]);
    if (paisNombre) loadAirports(paisNombre, 'destino');
    clearError('paisDestinoId');
  };
  const handleAirportCargaChange = (id) => {
    setField('aeropuertoCargaId', id || '');
    const item = airportsOrigen.find((a) => a._id === id);
    setField('aeropuertoCargaLabel', item ? `${item.iata} - ${item.nombre}` : '');
    clearError('aeropuertoCargaId');
  };
  const handleAirportDescargaChange = (id) => {
    setField('aeropuertoDescargaId', id || '');
    const item = airportsDestino.find((a) => a._id === id);
    setField('aeropuertoDescargaLabel', item ? `${item.iata} - ${item.nombre}` : '');
    clearError('aeropuertoDescargaId');
  };

  // ============= Detalle de paquetes =============
  const addDetalle = () => setDetalles((d) => [...d, emptyDetalle()]);
  const removeDetalle = (idx) => {
    setDetalles((d) => (d.length > 1 ? d.filter((_, i) => i !== idx) : d));
  };
  const updateDetalle = (idx, key, value) => {
    setDetalles((d) => d.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  };


  // ============= Validación =============
  const validate = () => {
    const err = {};
    if (!form.tipoOperacion) err.tipoOperacion = 'Selecciona el tipo de operación';
    if (!form.incotermId) err.incotermId = 'Selecciona un Incoterm';
    if (!form.clienteId) err.clienteId = 'Selecciona un cliente';
    if (!form.asesorId || form.asesorId === '') err.asesorId = 'Selecciona un asesor comercial o "No asignado"';
    if (!form.paisOrigenId) err.paisOrigenId = 'Selecciona el país de origen';
    if (!form.aeropuertoCargaId) err.aeropuertoCargaId = 'Selecciona el aeropuerto de carga';
    if (!form.paisDestinoId) err.paisDestinoId = 'Selecciona el país de destino';
    if (!form.aeropuertoDescargaId) err.aeropuertoDescargaId = 'Selecciona el aeropuerto de descarga';
    if (!form.numPiezas || Number(form.numPiezas) <= 0) err.numPiezas = 'Número de piezas debe ser mayor a 0';
    else if (totalPiezasDetalle > 0 && Number(form.numPiezas) !== totalPiezasDetalle) err.numPiezas = 'La suma de piezas del detalle debe coincidir con el total';
    if (!form.pesoTotal || Number(form.pesoTotal) <= 0) err.pesoTotal = 'Peso total debe ser mayor a 0';
    if (form.pesoVolumetrico === '' || Number(form.pesoVolumetrico) < 0) err.pesoVolumetrico = 'Peso volumétrico requerido';

    const detErrors = detalles.map((d) => {
      const e = {};
      if (!d.numPiezas || Number(d.numPiezas) <= 0) e.numPiezas = 'Requerido';
      if (!d.largo || Number(d.largo) <= 0) e.largo = 'Requerido';
      if (!d.ancho || Number(d.ancho) <= 0) e.ancho = 'Requerido';
      if (!d.alto || Number(d.alto) <= 0) e.alto = 'Requerido';
      if (!d.peso || Number(d.peso) <= 0) e.peso = 'Requerido';
      if (!d.tipoMercanciaId) e.tipoMercanciaId = 'Requerido';
      return e;
    });
    if (detErrors.some((e) => Object.keys(e).length > 0)) err.detalles = detErrors;
    if (detalles.length === 0) err.detallesGeneral = 'Agrega al menos una fila en el detalle';
    setErrores(err);
    return Object.keys(err).length === 0;
  };

  // ============= isFormComplete (para habilitar el botón en modal) =============
  const isFormComplete = useMemo(() => {
    if (!form.tipoOperacion) return false;
    if (!form.incotermId) return false;
    if (!form.clienteId) return false;
    if (!form.asesorId || form.asesorId === '') return false;
    if (!form.paisOrigenId) return false;
    if (!form.aeropuertoCargaId) return false;
    if (!form.paisDestinoId) return false;
    if (!form.aeropuertoDescargaId) return false;
    if (!form.numPiezas || Number(form.numPiezas) <= 0) return false;
    if (!form.pesoTotal || Number(form.pesoTotal) <= 0) return false;
    if (form.pesoVolumetrico === '' || Number(form.pesoVolumetrico) < 0) return false;
    if (detalles.length === 0) return false;
    return detalles.every(
      (d) =>
        d.numPiezas && Number(d.numPiezas) > 0 &&
        d.largo && Number(d.largo) > 0 &&
        d.ancho && Number(d.ancho) > 0 &&
        d.alto && Number(d.alto) > 0 &&
        d.peso && Number(d.peso) > 0 &&
        d.tipoMercanciaId,
    );
  }, [form, detalles]);

  // ============= Submit =============
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) {
      toast.error('Faltan datos', 'Revisa los campos marcados en rojo antes de guardar');
      return;
    }
    const payload = {
      tipoOperacion: form.tipoOperacion,
      incotermId: form.incotermId,
      descripcion: form.descripcion || undefined,
      // Opcional: solo se manda si el operador lo diligenció.
      valorMercancia:
        form.valorMercancia === '' ? undefined : Number(form.valorMercancia),
      monedaValorMercancia:
        form.valorMercancia === '' ? undefined : form.monedaValorMercancia,
      clienteId: form.clienteId,
      // Si eligió "No asignado" mandamos null para que el backend
      // no asigne un asesor. Si es un id real, lo mandamos tal cual.
      asesorId: form.asesorId === '__NONE__' ? null : form.asesorId,
      paisOrigenId: form.paisOrigenId,
      aeropuertoCargaId: form.aeropuertoCargaId,
      paisDestinoId: form.paisDestinoId,
      aeropuertoDescargaId: form.aeropuertoDescargaId,
      numPiezas: Number(form.numPiezas),
      pesoTotal: Number(form.pesoTotal),
      pesoVolumetrico: Number(form.pesoVolumetrico),
      detalles: detalles.map((d) => ({
        numPiezas: Number(d.numPiezas),
        largo: Number(d.largo),
        ancho: Number(d.ancho),
        alto: Number(d.alto),
        unidadDimension: d.unidadDimension,
        peso: Number(d.peso),
        unidadPeso: d.unidadPeso,
        tipoMercanciaId: d.tipoMercanciaId,
        apilable: d.apilable,
      })),
    };
    await onSubmit?.(payload);
  };

  // ============ Render según el modo ============
  // El modo modal: header sticky, contenido scrollable, footer sticky,
  // layout denso (sin SectionCard envolventes).
  // El modo normal: layout con SectionCard, header propio, submit al final.

  // === Sub-componentes reutilizables ===
  const inputClass = (hasError) =>
    `flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
      hasError ? 'border-destructive' : 'border-input'
    }`;

  const selectClass = (hasError) =>
    `flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
      hasError ? 'border-destructive' : 'border-input'
    }`;

  const renderFieldLabel = (text, required) => (
    <Label className="text-sm font-medium text-foreground">
      {text}
      {required && <span className="text-destructive"> *</span>}
    </Label>
  );

  const renderFieldError = (key) =>
    errores[key] ? <FieldError msg={errores[key]} /> : null;

  // 1) Información General
  const renderInfoGeneral = () => (
    <SectionBlock
      icon={Info}
      title="Información General"
      number={1}
      modal={modal}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          {renderFieldLabel('Tipo de operación', true)}
          <select
            value={form.tipoOperacion}
            onChange={(e) => { setField('tipoOperacion', e.target.value); clearError('tipoOperacion'); }}
            className={selectClass(errores.tipoOperacion)}
          >
            <option value="">— Selecciona —</option>
            <option value="Importación">Importación</option>
            <option value="Exportación">Exportación</option>
          </select>
          {renderFieldError('tipoOperacion')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Incoterm', true)}
          <select
            value={form.incotermId}
            onChange={(e) => { setField('incotermId', e.target.value); clearError('incotermId'); }}
            className={selectClass(errores.incotermId)}
          >
            <option value="">— Selecciona —</option>
            {incoterms.map((i) => (
              <option key={i._id} value={i._id}>
                {i.codigo} - {i.nombre}
              </option>
            ))}
          </select>
          {renderFieldError('incotermId')}
        </div>
      </div>

      {/* Valor de la mercancía — opcional. Se usa para cotizar el
          seguro y para los trámites aduaneros. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
        <div className="space-y-1.5 md:col-span-2">
          {renderFieldLabel('Valor aproximado de la mercancía')}
          <Input
            type="number"
            step="0.01"
            min={0}
            value={form.valorMercancia}
            onChange={(e) => setField('valorMercancia', e.target.value)}
            placeholder="Opcional — sirve para cotizar el seguro"
          />
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Moneda')}
          <select
            value={form.monedaValorMercancia}
            onChange={(e) => setField('monedaValorMercancia', e.target.value)}
            className={selectClass(false)}
          >
            {MONEDAS_VALOR_MERCANCIA.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>
      {!modal && (
        <div className="space-y-1.5 mt-3">
          {renderFieldLabel('Descripción')}
          <textarea
            rows={3}
            maxLength={500}
            value={form.descripcion}
            onChange={(e) => setField('descripcion', e.target.value)}
            placeholder="Detalle adicional sobre la operación (opcional, máx. 500 caracteres)"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <div className="text-xs text-muted-foreground text-right">
            {form.descripcion.length}/500
          </div>
        </div>
      )}
      {modal && (
        <div className="space-y-1.5 mt-3">
          {renderFieldLabel('Descripción')}
          <textarea
            rows={1}
            maxLength={500}
            value={form.descripcion}
            onChange={(e) => setField('descripcion', e.target.value)}
            placeholder="Detalle adicional (opcional, máx. 500 caracteres)"
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      )}
    </SectionBlock>
  );

  // 2) Cliente y Asesor
  const renderClienteAsesor = () => (
    <SectionBlock
      icon={User}
      title="Cliente y Asesor"
      number={2}
      modal={modal}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          {renderFieldLabel('Cliente', true)}
          <Autocomplete
            fetchOptions={async (q) => {
              const r = await searchClients(q);
              return r?.data || [];
            }}
            value={form.clienteId}
            onChange={handleClientChange}
            placeholder="Buscar por Nombre o NIT…"
            getOptionLabel={(item) => `${item.nombre} — ${item.nit}`}
            error={errores.clienteId}
            required
          />
          {form.clienteLabel && (
            <div className="text-xs text-muted-foreground">Seleccionado: {form.clienteLabel}</div>
          )}
          {renderFieldError('clienteId')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Asesor Comercial', true)}
          <select
            value={form.asesorId}
            onChange={(e) => { setField('asesorId', e.target.value); clearError('asesorId'); }}
            className={selectClass(errores.asesorId)}
          >
            <option value="">— Selecciona —</option>
            <option value="__NONE__">— No asignado —</option>
            {asesores.map((a) => (
              <option key={a._id} value={a._id}>{a.nombre}</option>
            ))}
          </select>
          {renderFieldError('asesorId')}
        </div>
      </div>
    </SectionBlock>
  );

  // 3) Ruta Aérea
  const renderRutaAerea = () => (
    <SectionBlock
      icon={MapPin}
      title="Ruta Aérea"
      number={3}
      modal={modal}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3 rounded-lg border border-border p-3 bg-card">
          <div className="text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" /> Origen
          </div>
          <div className="space-y-1.5">
            {renderFieldLabel('País de origen', true)}
            <Autocomplete
              fetchOptions={async (q) => {
                const r = await getCountriesWithAirports(q);
                return r?.data || [];
              }}
              value={form.paisOrigenId}
              onChange={handlePaisOrigenChange}
              placeholder="Escribe el país…"
              getOptionLabel={(item) => item.nombre || item._id || ''}
              error={errores.paisOrigenId}
              required
            />
            {renderFieldError('paisOrigenId')}
          </div>
          <div className="space-y-1.5">
            {renderFieldLabel('Aeropuerto de carga', true)}
            <select
              value={form.aeropuertoCargaId}
              onChange={(e) => handleAirportCargaChange(e.target.value)}
              disabled={!form.paisOrigenId || loadingAirportsOrigen}
              className={selectClass(errores.aeropuertoCargaId) + ' disabled:opacity-50'}
            >
              <option value="">
                {!form.paisOrigenId
                  ? 'Primero selecciona un país'
                  : loadingAirportsOrigen
                    ? 'Cargando aeropuertos…'
                    : airportsOrigen.length === 0
                      ? 'Sin aeropuertos activos en este país'
                      : '— Selecciona —'}
              </option>
              {airportsOrigen.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.iata} - {a.nombre}
                </option>
              ))}
            </select>
            {renderFieldError('aeropuertoCargaId')}
          </div>
        </div>
        <div className="space-y-3 rounded-lg border border-border p-3 bg-card">
          <div className="text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
            <ArrowRight className="h-3.5 w-3.5" /> Destino
          </div>
          <div className="space-y-1.5">
            {renderFieldLabel('País de destino', true)}
            <Autocomplete
              fetchOptions={async (q) => {
                const r = await getCountriesWithAirports(q);
                return r?.data || [];
              }}
              value={form.paisDestinoId}
              onChange={handlePaisDestinoChange}
              placeholder="Escribe el país…"
              getOptionLabel={(item) => item.nombre || item._id || ''}
              error={errores.paisDestinoId}
              required
            />
            {renderFieldError('paisDestinoId')}
          </div>
          <div className="space-y-1.5">
            {renderFieldLabel('Aeropuerto de descarga', true)}
            <select
              value={form.aeropuertoDescargaId}
              onChange={(e) => handleAirportDescargaChange(e.target.value)}
              disabled={!form.paisDestinoId || loadingAirportsDestino}
              className={selectClass(errores.aeropuertoDescargaId) + ' disabled:opacity-50'}
            >
              <option value="">
                {!form.paisDestinoId
                  ? 'Primero selecciona un país'
                  : loadingAirportsDestino
                    ? 'Cargando aeropuertos…'
                    : airportsDestino.length === 0
                      ? 'Sin aeropuertos activos en este país'
                      : '— Selecciona —'}
              </option>
              {airportsDestino.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.iata} - {a.nombre}
                </option>
              ))}
            </select>
            {renderFieldError('aeropuertoDescargaId')}
          </div>
        </div>
      </div>
    </SectionBlock>
  );

  // 5) Carga
  const renderCarga = () => (
    <SectionBlock
      icon={Plane}
      title="Carga"
      number={5}
      modal={modal}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          {renderFieldLabel('No. Piezas Total', true)}
          <Input
            type="number"
            min={1}
            value={form.numPiezas}
            readOnly
            className={errores.numPiezas ? 'border-destructive' : ''}
          />
          {renderFieldError('numPiezas')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Peso Total (kg)', true)}
          <Input
            type="number"
            step="0.01"
            min={0.01}
            value={form.pesoTotal}
            readOnly
            className={errores.pesoTotal ? 'border-destructive' : ''}
          />
          {renderFieldError('pesoTotal')}
        </div>
        <div className="space-y-1.5">
          {renderFieldLabel('Peso Volumétrico (kg)', true)}
          <Input
            type="number"
            step="0.01"
            min={0}
            value={form.pesoVolumetrico}
            readOnly
            className={errores.pesoVolumetrico ? 'border-destructive' : ''}
          />
          {renderFieldError('pesoVolumetrico')}
        </div>
      </div>
      {modal && (
        <div className="text-xs text-muted-foreground mt-2 space-y-1">
          <div>Suma detalles: <span className="font-semibold text-foreground">{totalPiezasDetalle}</span></div>
          <div>Peso total calculado: <span className="font-semibold text-foreground">{pesoTotalDetalle.toFixed(2)} kg</span></div>
          <div>Peso volumétrico calculado: <span className="font-semibold text-foreground">{pesoVolumetricoDetalle.toFixed(2)} kg</span></div>
          <div>
            Peso cobrable: <span className="font-semibold text-foreground">{carga.pesoCobrable.toFixed(2)} kg</span>
            <span className="ml-1">(el mayor entre el peso bruto y el volumétrico, factor 1:{FACTOR_VOLUMETRICO_AEREO})</span>
          </div>
          {totalPiezasDetalle > 0 && Number(form.numPiezas) !== totalPiezasDetalle && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-destructive">
              La suma de piezas del detalle no coincide con el total. Ajusta el valor para que sea {totalPiezasDetalle}.
            </div>
          )}
        </div>
      )}
    </SectionBlock>
  );

  // 4) Detalle de Paquetes
  const renderDetalle = () => (
    <SectionBlock
      icon={Package}
      title="Detalle de Paquetes"
      number={4}
      modal={modal}
      action={
        <Button type="button" variant="outline" size="sm" onClick={addDetalle}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Añadir fila
        </Button>
      }
    >
      {modal ? (
        // Tabla inline compacta
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
                <th className="py-2 px-2 font-medium">No. Piezas</th>
                <th className="py-2 px-2 font-medium">Largo</th>
                <th className="py-2 px-2 font-medium">Ancho</th>
                <th className="py-2 px-2 font-medium">Alto</th>
                <th className="py-2 px-2 font-medium">Unidad</th>
                <th className="py-2 px-2 font-medium">Peso</th>
                <th className="py-2 px-2 font-medium">Und. Peso</th>
                <th className="py-2 px-2 font-medium">Tipo Mercancía</th>
                <th className="py-2 px-2 font-medium">Apilable</th>
                <th className="py-2 px-1"></th>
              </tr>
            </thead>
            <tbody>
              {detalles.map((d, idx) => {
                const detErr = errores.detalles?.[idx] || {};
                return (
                  <tr key={idx} className="border-t border-border">
                    <td className="p-1.5">
                      <Input
                        type="number" min={1}
                        value={d.numPiezas}
                        onChange={(e) => updateDetalle(idx, 'numPiezas', e.target.value)}
                        className={`h-8 text-sm ${detErr.numPiezas ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number" step="0.01" min={0.01}
                        value={d.largo}
                        onChange={(e) => updateDetalle(idx, 'largo', e.target.value)}
                        className={`h-8 text-sm ${detErr.largo ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number" step="0.01" min={0.01}
                        value={d.ancho}
                        onChange={(e) => updateDetalle(idx, 'ancho', e.target.value)}
                        className={`h-8 text-sm ${detErr.ancho ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number" step="0.01" min={0.01}
                        value={d.alto}
                        onChange={(e) => updateDetalle(idx, 'alto', e.target.value)}
                        className={`h-8 text-sm ${detErr.alto ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <select
                        value={d.unidadDimension}
                        onChange={(e) => updateDetalle(idx, 'unidadDimension', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      >
                        <option value="cm">cm</option>
                        <option value="in">in</option>
                      </select>
                    </td>
                    <td className="p-1.5">
                      <Input
                        type="number" step="0.01" min={0.01}
                        value={d.peso}
                        onChange={(e) => updateDetalle(idx, 'peso', e.target.value)}
                        className={`h-8 text-sm ${detErr.peso ? 'border-destructive' : ''}`}
                      />
                    </td>
                    <td className="p-1.5">
                      <select
                        value={d.unidadPeso}
                        onChange={(e) => updateDetalle(idx, 'unidadPeso', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      >
                        <option value="kg">kg</option>
                        <option value="lb">lb</option>
                      </select>
                    </td>
                    <td className="p-1.5">
                      <select
                        value={d.tipoMercanciaId}
                        onChange={(e) => updateDetalle(idx, 'tipoMercanciaId', e.target.value)}
                        className={`h-8 w-full rounded-md border bg-background px-2 text-sm ${detErr.tipoMercanciaId ? 'border-destructive' : 'border-input'}`}
                      >
                        <option value="">— Tipo —</option>
                        {commodityTypes.map((c) => (
                          <option key={c._id} value={c._id}>{c.nombre}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-1.5">
                      <select
                        value={d.apilable}
                        onChange={(e) => updateDetalle(idx, 'apilable', e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                      >
                        <option value="Si">Sí</option>
                        <option value="No">No</option>
                      </select>
                    </td>
                    <td className="p-1.5 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDetalle(idx)}
                        disabled={detalles.length === 1}
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        title="Eliminar fila"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        // Layout standalone con cards
        <div className="space-y-3">
          {detalles.map((d, idx) => {
            const detErr = errores.detalles?.[idx] || {};
            return (
              <div key={idx} className="border border-border rounded-lg p-3 bg-muted/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">Paquete #{idx + 1}</div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeDetalle(idx)}
                    disabled={detalles.length === 1}
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    title="Eliminar fila"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Piezas *</Label>
                    <Input
                      type="number"
                      min={1}
                      value={d.numPiezas}
                      onChange={(e) => updateDetalle(idx, 'numPiezas', e.target.value)}
                      className={detErr.numPiezas ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Largo *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={d.largo}
                      onChange={(e) => updateDetalle(idx, 'largo', e.target.value)}
                      className={detErr.largo ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ancho *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={d.ancho}
                      onChange={(e) => updateDetalle(idx, 'ancho', e.target.value)}
                      className={detErr.ancho ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Alto *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={d.alto}
                      onChange={(e) => updateDetalle(idx, 'alto', e.target.value)}
                      className={detErr.alto ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Unidad</Label>
                    <select
                      value={d.unidadDimension}
                      onChange={(e) => updateDetalle(idx, 'unidadDimension', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="cm">cm</option>
                      <option value="in">in</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Peso *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={d.peso}
                      onChange={(e) => updateDetalle(idx, 'peso', e.target.value)}
                      className={detErr.peso ? 'border-destructive' : ''}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Unidad peso</Label>
                    <select
                      value={d.unidadPeso}
                      onChange={(e) => updateDetalle(idx, 'unidadPeso', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="kg">kg</option>
                      <option value="lb">lb</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Apilable</Label>
                    <select
                      value={d.apilable}
                      onChange={(e) => updateDetalle(idx, 'apilable', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="Si">Sí</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  <Label className="text-xs">Tipo de mercancía *</Label>
                  <select
                    value={d.tipoMercanciaId}
                    onChange={(e) => updateDetalle(idx, 'tipoMercanciaId', e.target.value)}
                    className={`flex h-10 w-full rounded-md border bg-background px-2 text-sm ${detErr.tipoMercanciaId ? 'border-destructive' : 'border-input'}`}
                  >
                    <option value="">— Selecciona —</option>
                    {commodityTypes.map((c) => (
                      <option key={c._id} value={c._id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
          <Button type="button" variant="outline" onClick={addDetalle} className="w-full">
            <Plus className="mr-2 h-4 w-4" /> Agregar paquete
          </Button>
          {errores.detallesGeneral && <FieldError msg={errores.detallesGeneral} />}
          <div className="text-xs text-muted-foreground text-right">
            Suma de piezas en el detalle: {totalPiezasDetalle}
          </div>
        </div>
      )}
    </SectionBlock>
  );

  // ============ Layout modal vs normal ============
  if (modal) {
    return (
      <form
        onSubmit={handleSubmit}
        className="flex flex-col h-full max-h-[85vh]"
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-background border-b border-border px-6 py-4 flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-lg">
            <Send className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold leading-tight">{modalTitle}</h2>
            <p className="text-xs text-muted-foreground">{modalSubtitle}</p>
          </div>
        </div>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {renderInfoGeneral()}
          {renderClienteAsesor()}
          {renderRutaAerea()}
          {renderDetalle()}
          {renderCarga()}
        </div>

        {/* Footer sticky */}
        <div className="sticky bottom-0 z-10 bg-background border-t border-border px-6 py-4 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            type="submit"
            disabled={submitting || !isFormComplete}
            title={!isFormComplete ? 'Completa todos los campos requeridos' : undefined}
          >
            <Save className="mr-2 h-4 w-4" />
            {submitting ? 'Guardando…' : submitLabel}
          </Button>
        </div>
      </form>
    );
  }

  // Layout normal (con SectionCards)
  const wrapperClass = compact ? 'space-y-3' : 'space-y-4';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={wrapperClass}
    >
      <form onSubmit={handleSubmit} className={wrapperClass}>
        {!hideHeader && (
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Nueva Solicitud Aérea</h1>
              <p className="text-sm text-muted-foreground">
                Complete las 5 secciones. Todos los catálogos se consultan al backend.
              </p>
            </div>
          </div>
        )}

        {renderInfoGeneral()}
        {renderClienteAsesor()}
        {renderRutaAerea()}
        {renderDetalle()}
        {renderCarga()}

        <div className="flex justify-end gap-2 pt-2">
          {showCancelButton && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {cancelLabel}
            </Button>
          )}
          <Button type="submit" disabled={submitting}>
            <Save className="mr-2 h-4 w-4" />
            {submitting ? 'Guardando…' : submitLabel}
          </Button>
        </div>
      </form>
    </motion.div>
  );
}

// ============ Sub-componentes de layout ============

function SectionBlock({ icon: Icon, title, number, children, modal, action }) {
  if (modal) {
    return (
      <section className="space-y-3">
        <header className="flex items-center justify-between border-b border-border pb-2">
          <div className="flex items-center gap-2">
            {Icon && (
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </span>
            )}
            <h3 className="text-xs font-bold uppercase tracking-wider text-foreground">
              {title}
            </h3>
          </div>
          {action}
        </header>
        <div>{children}</div>
      </section>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-3 text-lg">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary text-sm font-bold">
            {number}
          </span>
          {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function FieldError({ msg }) {
  return (
    <div className="flex items-center gap-1 text-xs text-destructive">
      <AlertCircle className="h-3 w-3" />
      <span>{msg}</span>
    </div>
  );
}
